import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert
} from '@mui/material';

/**
 * Edit Assessment Metadata Dialog
 * 
 * Allows editing assessment name and created date
 */
const EditAssessmentMetadataDialog = ({ 
  open, 
  onClose, 
  assessment,
  onSave 
}) => {
  const [name, setName] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (assessment) {
      setName(assessment.name || '');
      
      // Format created_at for date input (YYYY-MM-DD)
      if (assessment.created_at) {
        const date = new Date(assessment.created_at);
        const formatted = date.toISOString().split('T')[0];
        setCreatedAt(formatted);
      }
    }
  }, [assessment]);

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      setError('Assessment name is required');
      return;
    }
    
    if (!createdAt) {
      setError('Created date is required');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      await onSave({
        name: name.trim(),
        created_at: createdAt
      });
      
      onClose();
      
    } catch (error) {
      console.error('Error updating metadata:', error);
      setError(error.response?.data?.error || error.message || 'Failed to update assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError('');
      onClose();
    }
  };

  if (!assessment) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Edit Assessment Details
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Update the assessment name and created date
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Assessment Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            required
            helperText="Descriptive name for this assessment"
            sx={{ mb: 2 }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Created Date"
            type="date"
            fullWidth
            value={createdAt}
            onChange={(e) => setCreatedAt(e.target.value)}
            disabled={saving}
            required
            InputLabelProps={{
              shrink: true,
            }}
            helperText="When this assessment was originally created"
          />
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Changing the created date will affect when this assessment appears in historical reports and timelines.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={handleClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={saving || !name.trim() || !createdAt}
          sx={{ 
            backgroundColor: '#3498db',
            '&:hover': { backgroundColor: '#2980b9' }
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssessmentMetadataDialog;
